"use client"

import * as Icons from "@ant-design/icons"
import { ComponentConfig } from "@measured/puck"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface ButtonDiscoverProps extends CommonStylesProps {
  type: "primary" | "secondary"
  size?: "small" | "medium" | "large"
  icon?: keyof typeof Icons
  text?: string
}

const defaultStyles = {
  primary: {
    alignItems: "center",
    gap: "8px",
    padding: " 12px 8px",
    borderRadius: "100px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
    hoverTextColor: "#fff",
    backgroundColor: "#0B9444",
    hoverBgColor: "#FFFFFF",
    lineHeight: "22px",
  },
  secondary: {
    alignItems: "center",
    gap: "8px",
    padding: "12px 8px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#00A19A",
    hoverTextColor: "#fff",
    backgroundColor: "#fff",
    hoverBgColor: "#00A19A",
    border: "1px solid #31343e",
    lineHeight: "22px",
  },
}
const hoverStyles = {
  primary: {
    backgroundColor: "#FFFFFF",
    color: "#0B9444",
    transform: "scale(1.05)",
  },
  secondary: {
    backgroundColor: "#00A19A",
    color: "#fff",
    transform: "scale(1.05)",
  },
}

const sizeStyles = {
  small: {
    padding: "4px 8px",
    fontSize: "12px",
    lineHeight: "18px",
  },
  medium: {
    padding: "12px 8px 12px 16px",
    fontSize: "14px",
    lineHeight: "22px",
  },
  large: {
    padding: "12px 24px",
    fontSize: "16px",
    lineHeight: "24px",
  },
}

export const RenderConfig: ComponentConfig<ButtonDiscoverProps> = {
  render: ({ type, size, icon, text, className, styles, responsiveType }) => {
    const id = `button-${Math.random().toString(36).substr(2, 9)}`

    const buttonStyles = { ...defaultStyles[type], ...sizeStyles[size || "medium"] }
    const buttonHoverStyles = hoverStyles[type]
    const mergedStyles = {
      mobile: {
        ...buttonStyles,
        ...styles?.mobile,
        ":hover": buttonHoverStyles,
      },
      tablet: {
        ...buttonStyles,
        ...styles?.tablet,
        ":hover": buttonHoverStyles,
      },
      desktop: {
        ...buttonStyles,
        ...styles?.desktop,
        ":hover": buttonHoverStyles,
      },
    }

    const responsiveCSS = generateResponsiveCSS(id, mergedStyles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <button
          className={`
            ${id} 
            ${twMerge(
              className,
              "group flex items-center justify-between gap-2 relative overflow-hidden",
              "transition duration-300 ease-in-out",
              "hover:shadow-md border border-[#6670851F]",
              type === "primary" ? "hover:bg-white hover:text-[#0B9444]" : "hover:bg-[#00A19A] hover:text-white",
            )}`}
        >
          <span>{text}</span>
          <div
            className={`
              p-1 rounded-full bg-white h-6 w-6 flex items-center justify-center 
              group-hover:bg-[#0B9444] transition duration-300
            `}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition duration-300 group-hover:stroke-white"
            >
              <path
                d="M0.939941 6.99951H12.9995"
                stroke="#0B9444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="group-hover:stroke-white duration-300"
              />
              <path
                d="M8.7959 11.2037L12.9996 7.00006L8.7959 2.79639"
                stroke="#0B9444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="group-hover:stroke-white duration-300"
              />
            </svg>
          </div>
        </button>
      </>
    )
  },
}
